/**
 * SLIDESHOW CON PROGRAMACIÓN TEMPORAL
 * ==================================
 * JavaScript personalizado para manejar slides programados por fecha y hora
 * Compatible con Flickity slider
 * 
 * Funcionalidades:
 * - Verificación automática de fechas cada minuto
 * - Actualización dinámica de slides activos
 * - Indicadores visuales de estado
 * - Reinicialización automática del slider Flickity
 * 
 * @author Hang Ten Mexico
 * @version 1.0.0
 */

class ScheduledSlideshow {
  constructor() {
    this.slideshows = document.querySelectorAll('.t4s-slideshow.flickityt4s');
    this.checkInterval = 60000; // Verificar cada minuto
    this.debugMode = new URLSearchParams(window.location.search).has('debug');
    
    this.init();
  }

  init() {
    console.log('🎠 Inicializando Slideshow Programado v1.0.0');
    
    // Esperar a que Flickity esté inicializado
    if (typeof window.flickityt4s !== 'undefined') {
      this.checkScheduledSlides();
    } else {
      // Esperar a que el DOM y Flickity estén listos
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          this.checkScheduledSlides();
        }, 1000);
      });
    }
    
    // Configurar verificación periódica
    setInterval(() => {
      this.checkScheduledSlides();
    }, this.checkInterval);
    
    // Agregar indicadores de debug si está habilitado
    if (this.debugMode) {
      this.enableDebugMode();
    }
    
    // Agregar listeners para eventos de visibilidad
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkScheduledSlides();
      }
    });
  }

  checkScheduledSlides() {
    const currentTimestamp = Math.floor(Date.now() / 1000);
    
    this.slideshows.forEach(slideshow => {
      const slides = slideshow.querySelectorAll('.t4s-slideshow-item[data-publish-date], .t4s-slideshow-item[data-unpublish-date]');
      let visibilityChanged = false;
      
      slides.forEach(slide => {
        const publishDate = slide.dataset.publishDate;
        const unpublishDate = slide.dataset.unpublishDate;
        const wasVisible = slide.style.display !== 'none' && !slide.classList.contains('t4s-slide-hidden');
        let shouldBeVisible = true;
        
        // Verificar fecha de inicio
        if (publishDate) {
          const publishTimestamp = this.parseDate(publishDate);
          if (publishTimestamp && currentTimestamp < publishTimestamp) {
            shouldBeVisible = false;
          }
        }
        
        // Verificar fecha de fin
        if (unpublishDate && shouldBeVisible) {
          const unpublishTimestamp = this.parseDate(unpublishDate);
          if (unpublishTimestamp && currentTimestamp > unpublishTimestamp) {
            shouldBeVisible = false;
          }
        }
        
        // Aplicar cambios de visibilidad
        if (wasVisible !== shouldBeVisible) {
          visibilityChanged = true;
          
          if (shouldBeVisible) {
            this.showSlide(slide);
            this.notifySlideActivated(slide);
          } else {
            this.hideSlide(slide);
            this.notifySlideDeactivated(slide);
          }
        }
        
        // Actualizar indicadores visuales
        this.updateSlideIndicators(slide, shouldBeVisible, publishDate, unpublishDate, currentTimestamp);
      });
      
      // Si hubo cambios, reinicializar el slideshow Flickity
      if (visibilityChanged) {
        this.reinitializeFlickity(slideshow);
      }
    });
  }

  parseDate(dateString) {
    if (!dateString) return null;
    
    // Intentar parsear formato YYYY-MM-DD HH:MM o YYYY-MM-DD
    const dateStr = dateString.trim();
    let date;
    
    // Si tiene hora, usar formato completo
    if (dateStr.includes(' ')) {
      date = new Date(dateStr.replace(' ', 'T'));
    } else {
      // Si solo tiene fecha, agregar hora 00:00
      date = new Date(dateStr + 'T00:00:00');
    }
    
    // Verificar si la fecha es válida
    if (isNaN(date.getTime())) {
      console.warn('Fecha inválida:', dateString);
      return null;
    }
    
    return Math.floor(date.getTime() / 1000);
  }

  showSlide(slide) {
    slide.style.display = '';
    slide.classList.remove('t4s-slide-hidden');
    slide.setAttribute('data-scheduled-active', 'true');
    slide.setAttribute('data-just-activated', 'true');
    
    // Remover el atributo después de la animación
    setTimeout(() => {
      slide.removeAttribute('data-just-activated');
    }, 1000);
    
    if (this.debugMode) {
      console.log('✅ Slide activado:', slide.id || slide.className);
    }
  }

  hideSlide(slide) {
    slide.style.display = 'none';
    slide.classList.add('t4s-slide-hidden');
    slide.removeAttribute('data-scheduled-active');
    
    if (this.debugMode) {
      console.log('❌ Slide desactivado:', slide.id || slide.className);
    }
  }

  updateSlideIndicators(slide, isVisible, publishDate, unpublishDate, currentTimestamp) {
    // Remover indicadores existentes
    const existingIndicator = slide.querySelector('.slide-schedule-status');
    if (existingIndicator) {
      existingIndicator.remove();
    }
    
    let status = '';
    let statusClass = '';
    
    if (!isVisible) {
      if (publishDate) {
        const publishTimestamp = this.parseDate(publishDate);
        if (publishTimestamp && currentTimestamp < publishTimestamp) {
          status = 'Próximamente';
          statusClass = 'coming-soon';
        }
      }
      
      if (unpublishDate) {
        const unpublishTimestamp = this.parseDate(unpublishDate);
        if (unpublishTimestamp && currentTimestamp > unpublishTimestamp) {
          status = 'Expirado';
          statusClass = 'expired';
        }
      }
    } else {
      status = 'Activo';
      statusClass = 'active';
      
      // Verificar si está próximo a expirar (24 horas)
      if (unpublishDate) {
        const unpublishTimestamp = this.parseDate(unpublishDate);
        if (unpublishTimestamp) {
          const hoursUntilExpiry = (unpublishTimestamp - currentTimestamp) / 3600;
          
          if (hoursUntilExpiry <= 24 && hoursUntilExpiry > 0) {
            slide.setAttribute('data-expiring-soon', 'true');
          } else {
            slide.removeAttribute('data-expiring-soon');
          }
        }
      }
    }
    
    // Crear nuevo indicador solo en modo debug o si no está visible
    if (status && (this.debugMode || !isVisible)) {
      const indicator = document.createElement('div');
      indicator.className = `slide-schedule-status ${statusClass}`;
      indicator.textContent = status;
      
      if (this.debugMode) {
        indicator.setAttribute('title', `Inicio: ${publishDate || 'N/A'}\nFin: ${unpublishDate || 'N/A'}`);
      }
      
      slide.appendChild(indicator);
    }
  }

  reinitializeFlickity(slideshow) {
    // Obtener la instancia de Flickity si existe
    if (window.flickityt4s && slideshow.flickityt4s) {
      try {
        // Destruir la instancia actual
        if (slideshow.flickityt4s.destroy) {
          slideshow.flickityt4s.destroy();
        }
        
        // Reinicializar Flickity después de un breve delay
        setTimeout(() => {
          if (window.flickityt4s.init) {
            window.flickityt4s.init();
          }
        }, 100);
      } catch (e) {
        console.warn('Error al reinicializar Flickity:', e);
        // Intentar recargar la página si hay problemas
        if (this.debugMode) {
          console.log('Reiniciando slideshow...');
        }
      }
    } else {
      // Disparar evento personalizado para que otros scripts manejen la reinicialización
      const event = new CustomEvent('slideshow:update', {
        detail: { slideshow: slideshow }
      });
      
      slideshow.dispatchEvent(event);
    }
    
    // Actualizar contadores y navegación
    this.updateSlideshowNavigation(slideshow);
  }

  updateSlideshowNavigation(slideshow) {
    const visibleSlides = slideshow.querySelectorAll('.t4s-slideshow-item:not(.t4s-slide-hidden):not([style*="display: none"])');
    const counters = slideshow.querySelectorAll('.flickityt4s-page-dots, .slider-counter--total');
    
    counters.forEach(counter => {
      if (counter.textContent !== undefined) {
        counter.textContent = visibleSlides.length;
      }
    });
    
    // Actualizar dots de Flickity si están disponibles
    if (slideshow.flickityt4s && slideshow.flickityt4s.updateDots) {
      setTimeout(() => {
        try {
          slideshow.flickityt4s.updateDots();
        } catch (e) {
          // Ignorar errores si Flickity no está completamente inicializado
        }
      }, 200);
    }
  }

  notifySlideActivated(slide) {
    if (this.debugMode && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Slide Activado', {
        body: 'Un slide programado se ha activado automáticamente',
        icon: '/favicon.ico'
      });
    }
  }

  notifySlideDeactivated(slide) {
    if (this.debugMode && 'Notification' in window && Notification.permission === 'granted') {
      new Notification('Slide Desactivado', {
        body: 'Un slide programado se ha desactivado automáticamente',
        icon: '/favicon.ico'
      });
    }
  }

  enableDebugMode() {
    document.body.setAttribute('data-debug', 'true');
    
    // Agregar información de debug al console
    console.log('🐛 Modo Debug habilitado para Slideshow Programado');
    console.log('ℹ️ Parámetros disponibles:');
    console.log('  - ?debug=1 : Mostrar indicadores de estado');
    console.log('  - Verificación automática cada', this.checkInterval / 1000, 'segundos');
    
    // Solicitar permisos de notificación si no están concedidos
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  // Método público para verificación manual
  static forceCheck() {
    if (window.scheduledSlideshow) {
      window.scheduledSlideshow.checkScheduledSlides();
      console.log('🔄 Verificación manual ejecutada');
    }
  }

  // Método público para obtener información de slides
  static getSlideInfo() {
    if (window.scheduledSlideshow) {
      const info = {};
      window.scheduledSlideshow.slideshows.forEach((slideshow, index) => {
        const slides = slideshow.querySelectorAll('.t4s-slideshow-item[data-publish-date], .t4s-slideshow-item[data-unpublish-date]');
        info[`slideshow_${index}`] = {
          totalSlides: slides.length,
          activeSlides: slideshow.querySelectorAll('.t4s-slideshow-item:not(.t4s-slide-hidden)').length,
          slides: Array.from(slides).map(slide => ({
            id: slide.id,
            publishDate: slide.dataset.publishDate,
            unpublishDate: slide.dataset.unpublishDate,
            isActive: slide.hasAttribute('data-scheduled-active')
          }))
        };
      });
      return info;
    }
    return null;
  }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      window.scheduledSlideshow = new ScheduledSlideshow();
    }, 500);
  });
} else {
  setTimeout(() => {
    window.scheduledSlideshow = new ScheduledSlideshow();
  }, 500);
}

// Exportar métodos útiles para la consola
window.ScheduledSlideshow = ScheduledSlideshow;

// Agregar ayudas para desarrolladores
if (new URLSearchParams(window.location.search).has('debug')) {
  console.log('🎠 Slideshow Programado - Comandos disponibles:');
  console.log('  ScheduledSlideshow.forceCheck() - Forzar verificación');
  console.log('  ScheduledSlideshow.getSlideInfo() - Obtener información de slides');
}

